import { Controller } from "@hotwired/stimulus"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  requiredMessage: "This field is required",
  invalidIbanMessage: "Please enter a valid IBAN",
  invalidDateMessage: "Please enter a valid date"
}

export default class extends Controller {
  static values = {
    translations: { type: Object, default: {} }
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.setupFormValidation()
  }

  setupFormValidation() {
    const form = document.getElementById("bank_account_form")
    if (!form) return

    form.addEventListener("submit", (event) => {
      this.clearAllErrors()
      if (!this.validateForm()) {
        event.preventDefault()
      }
    })

    // Clear error when user starts typing
    this.element.querySelectorAll("input, select, textarea").forEach((field) => {
      field.addEventListener("input", () => this.clearFieldError(field))
      field.addEventListener("change", () => this.clearFieldError(field))
    })
  }

  validateForm() {
    let isValid = true
    const fields = this.element.querySelectorAll("input[required], select[required], textarea[required]")

    fields.forEach((field) => {
      if (!this.validateField(field)) {
        isValid = false
      }
    })

    return isValid
  }

  validateField(field) {
    const value = field.value.trim()
    const container = field.closest(".lc-input")

    // Check required
    if (field.required && !value) {
      this.showError(container, field, this.#texts.requiredMessage)
      return false
    }

    // Check pattern (for IBAN)
    if (field.pattern && value) {
      const regex = new RegExp(`^${field.pattern}$`)
      if (!regex.test(value)) {
        const message = this.getPatternMessage(field)
        this.showError(container, field, message)
        return false
      }
    }

    // Check validity
    if (!field.validity.valid) {
      const message = field.title || this.getValidityMessage(field)
      this.showError(container, field, message)
      return false
    }

    return true
  }

  getPatternMessage(field) {
    const name = field.name.toLowerCase()
    if (name.includes("iban")) {
      return this.#texts.invalidIbanMessage
    } else if (name.includes("date")) {
      return this.#texts.invalidDateMessage
    }
    return field.title || "Invalid format"
  }

  getValidityMessage(field) {
    if (field.validity.valueMissing) return this.#texts.requiredMessage
    if (field.validity.patternMismatch) return this.getPatternMessage(field)
    return "Invalid value"
  }

  showError(container, field, message) {
    if (!container) return

    // Add error styling to the field
    field.classList.add("is-invalid")

    // Check if error element already exists
    let errorEl = container.querySelector(".form-hint.text-danger")
    if (!errorEl) {
      errorEl = document.createElement("div")
      errorEl.className = "form-hint text-danger d-flex align-items-center"

      const iconSpan = document.createElement("span")
      iconSpan.className = "material-symbols-outlined fs-6 me-1"
      iconSpan.textContent = "error"

      const messageSpan = document.createElement("span")
      messageSpan.className = "error-message"

      errorEl.appendChild(iconSpan)
      errorEl.appendChild(messageSpan)
      container.appendChild(errorEl)
    }

    errorEl.querySelector(".error-message").textContent = message
  }

  clearFieldError(field) {
    field.classList.remove("is-invalid")
    const container = field.closest(".lc-input")
    if (container) {
      const errorEl = container.querySelector(".form-hint.text-danger")
      if (errorEl) {
        errorEl.remove()
      }
    }
  }

  clearAllErrors() {
    this.element.querySelectorAll(".is-invalid").forEach((field) => {
      field.classList.remove("is-invalid")
    })
    this.element.querySelectorAll(".form-hint.text-danger").forEach((el) => {
      el.remove()
    })
  }
}
